import React, { useState, useEffect } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { ShoppingCart, AlertCircle } from 'lucide-react';

const BulkOrderMatrix = ({ product, onAddToCart, loading }) => {
  const [quantities, setQuantities] = useState({});
  const [errors, setErrors] = useState({});

  // Determine sizes based on sizing_type
  const getSizes = () => {
    if (product?.sizing_type === 'onesize') {
      return ['OS'];
    } else if (product?.sizing_type === 'ladyfit') {
      return ['S', 'M', 'L', 'XL'];
    } else if (product?.sizing_type === 'kids') {
      return ['1-2', '3-4', '5-6', '7-8', '9-10', '11-12'];
    } else if (product?.sizing_type === 'combined') {
      return ['S/M', 'L/XL'];
    }
    return ['S', 'M', 'L', 'XL', 'XXL'];
  };

  const sizes = getSizes();
  const isOneSize = product?.sizing_type === 'onesize';
  const isKids = product?.sizing_type === 'kids';
  const isCombined = product?.sizing_type === 'combined';

  // Create inventory map with quantity, eta, and barcode
  const inventoryMap = product?.inventory?.reduce((acc, inv) => {
    acc[inv.size] = { quantity: inv.quantity, eta: inv.eta, barcode: inv.barcode };
    return acc;
  }, {}) || {};

  useEffect(() => {
    // Initialize quantities
    const initial = {};
    getSizes().forEach(size => {
      initial[size] = 0;
    });
    setQuantities(initial);
  }, [product?.id, product?.sizing_type]);

  const handleQuantityChange = (size, value) => {
    const numValue = parseInt(value) || 0;
    const inv = inventoryMap[size] || { quantity: 0, eta: null };
    const available = inv.quantity;
    const hasEta = inv.eta;
    const isPreOrder = product?.is_coming_soon;
    
    // Allow pre-orders and backorders without stock limit
    if (numValue > available && !hasEta && !isPreOrder) {
      setErrors(prev => ({ ...prev, [size]: `Max ${available} available` }));
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[size];
        return newErrors;
      });
    }

    setQuantities(prev => ({
      ...prev,
      [size]: (isPreOrder || hasEta) ? Math.max(0, numValue) : Math.min(Math.max(0, numValue), available)
    }));
  };

  const totalQuantity = Object.values(quantities).reduce((a, b) => a + b, 0);
  const totalPrice = totalQuantity * (product?.price || 0);

  const handleAddToCart = () => {
    const sizesWithQty = {};
    Object.entries(quantities).forEach(([size, qty]) => {
      if (qty > 0) {
        sizesWithQty[size] = qty;
      }
    });
    
    if (Object.keys(sizesWithQty).length > 0) {
      onAddToCart(sizesWithQty);
    }
  };

  const clearAll = () => {
    const reset = {};
    sizes.forEach(size => {
      reset[size] = 0;
    });
    setQuantities(reset);
    setErrors({});
  };

  return (
    <div className="border border-[#E2E8F0] bg-white" data-testid="bulk-order-matrix">
      {/* Header */}
      <div className="bg-[#F8FAFC] px-6 py-4 border-b border-[#E2E8F0]">
        <h3 className="text-lg font-semibold text-[#0F172A]">
          {isOneSize ? 'Select Quantity' : 'Select Quantities by Size'}
        </h3>
        <p className="text-sm text-[#64748B] mt-1">
          {isOneSize ? 'One size fits all' : isKids ? 'Kids sizing (age range)' : isCombined ? 'Combined sizing (S/M, L/XL)' : product?.sizing_type === 'ladyfit' ? 'Lady-fit sizing' : 'Unisex sizing'}
        </p>
      </div>

      {/* Size Grid */}
      <div className="p-6">
        <div className={`grid gap-4 mb-6 ${isOneSize ? 'grid-cols-1 max-w-xs mx-auto' : isCombined ? 'grid-cols-2 max-w-md mx-auto' : isKids ? 'grid-cols-6' : 'grid-cols-5'}`}>
          {sizes.map(size => {
            const inv = inventoryMap[size] || { quantity: 0, eta: null, barcode: null };
            const available = inv.quantity;
            const eta = inv.eta;
            const barcode = inv.barcode;
            const isOutOfStock = available === 0;
            const canBackorder = isOutOfStock && eta;
            const isPreOrder = product?.is_coming_soon;
            
            return (
              <div key={size} className="text-center">
                <div className="text-sm font-semibold text-[#0F172A] mb-2">
                  {size === 'OS' ? 'One Size' : size}
                </div>
                <div className={`text-xs mb-1 ${isPreOrder ? 'text-blue-600 font-medium' : available > 0 ? 'text-[#64748B]' : 'text-red-500'}`}>
                  {isPreOrder ? 'Pre-Order' : available > 0 ? `${available} avail.` : 'Out of stock'}
                </div>
                {barcode && (
                  <div className="text-[10px] text-gray-400 mb-1 font-mono truncate hidden sm:block" title={barcode}>
                    {barcode}
                  </div>
                )}
                {canBackorder && !isPreOrder && (
                  <div className="text-xs text-blue-600 mb-1">
                    Back: {new Date(eta).toLocaleDateString()}
                  </div>
                )}
                <Input
                  type="number"
                  min="0"
                  max={isPreOrder ? undefined : (available > 0 ? available : undefined)}
                  value={quantities[size] || 0}
                  onChange={(e) => handleQuantityChange(size, e.target.value)}
                  disabled={isOutOfStock && !canBackorder && !isPreOrder}
                  placeholder={isPreOrder ? 'Pre-Order' : (canBackorder ? 'Backorder' : '')}
                  className={`text-center h-12 text-lg font-medium ${
                    errors[size] ? 'border-red-500 focus:border-red-500' : 'focus:border-[#002FA7]'
                  } ${isPreOrder ? 'bg-blue-50 border-blue-200' : ''} ${
                    !isPreOrder && isOutOfStock ? (canBackorder ? 'bg-blue-50 border-blue-200' : 'bg-gray-100') : ''}`}
                  data-testid={`qty-input-${size}`}
                />
                {errors[size] && (
                  <div className="flex items-center justify-center gap-1 text-xs text-red-500 mt-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors[size]}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Summary */}
        <div className="border-t border-[#E2E8F0] pt-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <span className="text-sm text-[#64748B]">Total Units:</span>
              <span className="ml-2 text-lg font-semibold text-[#0F172A]" data-testid="total-units">
                {totalQuantity}
              </span>
            </div>
            <div>
              <span className="text-sm text-[#64748B]">Unit Price:</span>
              <span className="ml-2 font-mono text-[#0F172A]">
                ${product?.price?.toFixed(2)}
              </span>
            </div>
          </div>
          
          <div className="flex justify-between items-center mb-6 p-4 bg-[#F8FAFC]">
            <span className="text-sm font-medium text-[#0F172A]">Subtotal:</span>
            <span className="text-2xl font-bold text-[#002FA7] font-mono" data-testid="subtotal">
              ${totalPrice.toFixed(2)}
            </span>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={clearAll}
              className="flex-1 border-[#E2E8F0] text-[#64748B] hover:bg-[#F8FAFC]"
              data-testid="clear-btn"
            >
              Clear
            </Button>
            <Button
              onClick={handleAddToCart}
              disabled={totalQuantity === 0 || loading}
              className="flex-[2] bg-[#002FA7] hover:bg-[#0025a0] text-white font-semibold"
              data-testid="add-to-cart-btn"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              {loading ? 'Adding...' : 'Add to Cart'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkOrderMatrix;
